import { Fragment, memo, useCallback, useMemo } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { PrivateWorkspaceAgentPanelMessagePillChip } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentPanelMessagePillChip';
import { PrivateWorkspaceAgentPanelMessageSkillMoniker } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentPanelMessageSkillMoniker';
import { WorkspaceAgentUnknownMonikerPill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentUnknownMonikerPill';
import { WorkspaceAgentHeroUserPromptBubble } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/messages/WorkspaceAgentHeroUserPromptBubble';
import { normalizeSingleLineMarkdownTables } from '~/components/v2/components/workspace/workspace-agent-panel/markdown/normalizeSingleLineMarkdownTables';
import { PRIVATE_WORKSPACE_AGENT_MARKDOWN_COMPONENTS } from '~/components/v2/components/workspace/workspace-agent-panel/markdown/privateWorkspaceAgentMarkdownComponents';
import { remarkWorkspaceAgentPill } from '~/components/v2/components/workspace/workspace-agent-panel/markdown/remarkWorkspaceAgentPill';
import { WorkspaceAgentMarkdownPillsProvider } from '~/components/v2/components/workspace/workspace-agent-panel/markdown/WorkspaceAgentMarkdownPillsProvider';
import { parseWorkspaceAgentStructuredDisplay } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/displayMetadata';
import { type WorkspaceAgentMessagePill } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';
import { type WorkspaceAgentMessage } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentMessage';
import { findWorkspaceAgentMessagePill } from '~/components/v2/components/workspace/workspace-agent-panel/utils/findWorkspaceAgentMessagePill';
import { resolveWorkspaceAgentDisplayIcon } from '~/components/v2/components/workspace/workspace-agent-panel/utils/resolveWorkspaceAgentDisplayIcon';
import { splitPillMonikers } from '~/components/v2/components/workspace/workspace-agent-panel/utils/splitPillMonikers';

export type PrivateWorkspaceAgentPanelMessageBodyProps = {
  /** Mid-stream, a moniker whose pill has not arrived yet is not a broken reference. */
  isStreaming: boolean;
  message: WorkspaceAgentMessage;
  /** Parsed once by the list; parsing here would re-run zod over the whole history per streamed token. */
  pills: readonly WorkspaceAgentMessagePill[];
};

export const PrivateWorkspaceAgentPanelMessageBody = memo(
  ({ isStreaming, message, pills }: PrivateWorkspaceAgentPanelMessageBodyProps) => {
    const rawContent = message.content ?? '';
    const structuredDisplay = useMemo(
      () => (message.role === 'user' ? parseWorkspaceAgentStructuredDisplay(message.metadata) : null),
      [message.metadata, message.role],
    );
    const markdownContent = useMemo(() => normalizeSingleLineMarkdownTables(rawContent), [rawContent]);
    const pillRemarkPlugin = useCallback(() => remarkWorkspaceAgentPill(pills), [pills]);
    const remarkPlugins = useMemo(() => [remarkGfm, pillRemarkPlugin], [pillRemarkPlugin]);

    if (message.role === 'user' && structuredDisplay) {
      const Icon = resolveWorkspaceAgentDisplayIcon(structuredDisplay.icon);

      return (
        <div data-testid="WORKSPACE_AGENT_USER_MESSAGE_BODY">
          {rawContent ? <span className="sr-only">{rawContent}</span> : null}
          <WorkspaceAgentHeroUserPromptBubble
            description={structuredDisplay.description}
            icon={Icon}
            title={structuredDisplay.title}
          />
        </div>
      );
    }

    if (!rawContent) {
      // A user's pills-only send never gets here — the list omits the bubble when `displayHasBubble` is false.
      return <span className="text-12 text-gray-10">(no content)</span>;
    }

    // Only the agent writes markdown. Formatting what the user typed would mangle literal
    // asterisks and underscores, so their text stays verbatim.
    if (message.role === 'user') {
      return (
        <div
          className="whitespace-pre-wrap break-words text-14 text-gray-12"
          data-testid="WORKSPACE_AGENT_USER_MESSAGE_BODY"
        >
          {splitPillMonikers(rawContent).map((segment, index) => {
            if (segment.type === 'text') {
              return <Fragment key={`text-${index}`}>{segment.value}</Fragment>;
            }

            if (segment.type === 'unknownMoniker') {
              return (
                <WorkspaceAgentUnknownMonikerPill
                  key={`unknown-${index}-${segment.monikerType}-${segment.monikerId}`}
                  monikerId={segment.monikerId}
                  monikerType={segment.monikerType}
                />
              );
            }

            if (segment.entityType === 'skill') {
              return (
                <PrivateWorkspaceAgentPanelMessageSkillMoniker
                  key={`skill-${index}-${segment.monikerId}`}
                  skillId={segment.monikerId}
                />
              );
            }

            // Pills with no moniker render above the bubble instead (see `partitionWorkspaceAgentMessagePills`).
            // An unresolvable moniker still gets a chip rather than a hole in the sentence.
            return (
              <PrivateWorkspaceAgentPanelMessagePillChip
                isStreaming={isStreaming}
                key={`pill-${index}-${segment.monikerId}`}
                pill={findWorkspaceAgentMessagePill(pills, segment.monikerId)}
                rawId={segment.monikerId}
              />
            );
          })}
        </div>
      );
    }

    return (
      <div className="break-words text-14 text-gray-12">
        <WorkspaceAgentMarkdownPillsProvider isStreaming={isStreaming} pills={pills}>
          <Markdown components={PRIVATE_WORKSPACE_AGENT_MARKDOWN_COMPONENTS} remarkPlugins={remarkPlugins}>
            {markdownContent}
          </Markdown>
        </WorkspaceAgentMarkdownPillsProvider>
      </div>
    );
  },
);

PrivateWorkspaceAgentPanelMessageBody.displayName = 'PrivateWorkspaceAgentPanelMessageBody';
